import { useEffect, useState } from 'react'

import { VoicePlayerControl } from './VoicePlayerControl'

type Props = {
  uri: string
  bgcolor?: string
  color?: string
}

export function VoicePurePlayer({ uri, bgcolor, color }: Props) {
  const { play, pause, playing } = useAudio({ uri })

  return (
    <VoicePlayerControl
      playing={playing}
      onClickPlay={play}
      onClickPause={pause}
      bgcolor={bgcolor}
      color={color}
    />
  )
}

/**
 * @description useAudio
 * Audio objectを利用する際のカスタムフック
 *
 * @param {string} uri オーディオのurl
 *
 * @returns {boolean} playing 再生しているかどうかの状態
 * @returns {() => void} play 再生
 * @returns {() => void} pause 一時停止
 * @returns {() => void} stop 停止
 * @returns {() => void} togglePlay 再生/一時停止の切り替え
 */

export const useAudio = ({ uri }: { uri: string }) => {
  const [audio] = useState(new Audio(uri))
  const [playing, setPlaying] = useState(false)

  /**
   * @description
   * audio.pausedなどの変更をコンポーネント側で利用するため
   * audioオブジェクトの再生・停止・終了毎にplayingの状態を更新する
   */
  useEffect(() => {
    if (!uri) return

    audio.addEventListener('play', () => setPlaying(true))
    audio.addEventListener('pause', () => setPlaying(false))
    audio.addEventListener('ended', () => setPlaying(false))
    return () => {
      audio.removeEventListener('play', () => setPlaying(true))
      audio.removeEventListener('pause', () => setPlaying(false))
      audio.removeEventListener('ended', () => setPlaying(false))
      audio.pause()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri])

  if (typeof window === 'undefined' || !uri)
    return {
      playing: false,
      play() {},
      pause() {},
      stop() {},
      togglePlay() {},
    }

  return {
    playing,
    play() {
      audio.play()
    },
    pause() {
      audio.pause()
    },
    stop() {
      audio.pause()
      audio.currentTime = 0
    },
    togglePlay() {
      if (playing) return audio.pause()
      audio.play()
    },
  }
}
